import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { toast } from "sonner";
import { Chrome, Copy, RefreshCw, Trash2, ShieldCheck, Download } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExtensionSection = () => {
  const [pairing, setPairing] = useState(null); // {pairing_code, expires_at}
  const [creating, setCreating] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [zipInfo, setZipInfo] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/extension/download/info`)
      .then(({ data }) => setZipInfo(data))
      .catch(() => setZipInfo(null));
  }, []);

  const loadTokens = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/extension/tokens`, { withCredentials: true });
      setTokens(Array.isArray(data) ? data : []);
    } catch (e) {
      // silently — extension endpoints are optional
    }
  }, []);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  const generateCode = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/extension/pair/init`, {}, { withCredentials: true }
      );
      setPairing(data);
      toast.success("Eşleme kodu oluşturuldu");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kod oluşturulamadı");
    }
    setCreating(false);
  };

  const revoke = async (token_id) => {
    if (!window.confirm("Bu eklenti bağlantısını kaldırmak istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API_URL}/api/extension/tokens/${token_id}`, { withCredentials: true });
      toast.success("Bağlantı kaldırıldı");
      loadTokens();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaldırılamadı");
    }
  };

  const copy = (s) => { navigator.clipboard.writeText(s); toast.success("Kopyalandı"); };

  const remaining = pairing
    ? Math.max(0, Math.floor((new Date(pairing.expires_at).getTime() - now) / 1000))
    : 0;

  return (
    <Card data-testid="extension-settings-card" className="bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[#0A0A0A] dark:text-[#FAFAFA]">
          <Chrome className="w-5 h-5 text-[#F59E0B]" />
          Chrome Eklentisi ile Aktarım
        </CardTitle>
        <CardDescription className="text-[#52525B] dark:text-[#A1A1AA]">
          İlan sitelerinde açtığınız sayfaları tek tıkla önizleme ekranına aktarır.
          Otomatik kayıt yok — her ilan sizin onayınızla eklenir.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Kurulum */}
        <div>
          <h3 className="text-sm font-semibold mb-2">1. Eklentiyi indirin ve yükleyin</h3>

          <a
            href={`${API_URL}/api/extension/download`}
            download
            data-testid="extension-download-zip"
            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-[#0A0A0A] dark:bg-[#FAFAFA] text-white dark:text-[#0A0A0A] hover:opacity-90 transition-opacity text-sm font-medium mb-3"
          >
            <Download className="w-4 h-4" />
            Chrome Eklentisini İndir
            {zipInfo && (
              <span className="text-[11px] opacity-70 font-normal">
                (v{zipInfo.version || "?"} · {(zipInfo.size / 1024).toFixed(1)} KB)
              </span>
            )}
          </a>

          {zipInfo && (
            <div
              data-testid="extension-zip-checksum"
              className="text-[10px] font-mono text-[#52525B] dark:text-[#71717A] mb-3 break-all"
            >
              SHA-256: {zipInfo.sha256}
            </div>
          )}

          <ol className="text-xs text-[#52525B] dark:text-[#A1A1AA] space-y-1 list-decimal pl-4">
            <li>İndirdiğiniz <code>emlak-takip-chrome-extension.zip</code> dosyasını bir klasöre çıkarın.</li>
            <li>Chrome'da <code className="px-1 bg-[#F9FAFB] dark:bg-[#27272A] rounded">chrome://extensions</code> adresini açın.</li>
            <li>Sağ üstten <b>Geliştirici Modu</b>'nu (Developer mode) açın.</li>
            <li><b>"Paketlenmemiş öğe yükle"</b> (Load unpacked) tıklayın ve <b>manifest.json'un bulunduğu</b> çıkarılan klasörü seçin.</li>
            <li>Chrome araç çubuğunda "Emlak Takip'e Aktar" ikonu görünmelidir.</li>
          </ol>
        </div>

        {/* Eşleme */}
        <div>
          <h3 className="text-sm font-semibold mb-2">2. Uygulamayı eklentiye bağlayın</h3>

          {!pairing ? (
            <Button
              data-testid="extension-generate-code-button"
              onClick={generateCode}
              disabled={creating}
              className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {creating ? "Oluşturuluyor..." : "Eşleme Kodu Oluştur"}
            </Button>
          ) : (
            <div className="rounded border border-[#F59E0B]/40 bg-[#FEF3C7] dark:bg-[#F59E0B]/10 p-4 space-y-3">
              <div className="text-xs uppercase tracking-wider text-[#B45309] dark:text-[#F59E0B] font-semibold">
                Eşleme Kodu (tek kullanımlık)
              </div>
              <div className="flex items-center gap-3">
                <code
                  data-testid="extension-pairing-code"
                  className="text-2xl font-bold tracking-widest text-[#0A0A0A] dark:text-[#FAFAFA] flex-1"
                >
                  {pairing.pairing_code}
                </code>
                <Button variant="outline" size="sm" onClick={() => copy(pairing.pairing_code)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className="text-xs text-[#52525B] dark:text-[#A1A1AA]">
                {remaining > 0
                  ? <>Kod <b>{Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}</b> içinde geçersiz olur.</>
                  : <>Kod süresi doldu. Yeni kod oluşturun.</>}
              </div>
              <ol className="text-xs text-[#52525B] dark:text-[#A1A1AA] list-decimal pl-4 space-y-0.5">
                <li>Chrome araç çubuğundaki <b>Emlak Takip'e Aktar</b> ikonuna tıklayın.</li>
                <li>Uygulama seçim listesinden <b>Preview</b> ya da <b>Production</b> seçin.</li>
                <li>Kodu yapıştırın ve <b>Eşle</b>'ye basın.</li>
              </ol>
              <Button
                variant="outline"
                size="sm"
                onClick={generateCode}
                disabled={creating}
              >
                Yeni Kod
              </Button>
            </div>
          )}
        </div>

        {/* Aktif eklentiler */}
        <div>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Bağlı Eklentiler
          </h3>
          {tokens.length === 0 ? (
            <div className="text-xs text-[#52525B] dark:text-[#A1A1AA] italic">
              Henüz bağlı bir eklenti yok.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="extension-token-list">
              {tokens.map((t) => (
                <li
                  key={t.token_id}
                  data-testid={`extension-token-${t.token_id}`}
                  className="flex items-center gap-2 p-2 rounded border border-[#E4E4E7] dark:border-[#27272A] bg-[#F9FAFB] dark:bg-[#09090B]"
                >
                  <Chrome className="w-4 h-4 text-[#F59E0B] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{t.extension_name || "Chrome Extension"}</div>
                    <div className="text-[11px] text-[#52525B] dark:text-[#A1A1AA]">
                      Eşlendi: {new Date(t.created_at).toLocaleString()}
                      {t.last_used_at && <> · Son kullanım: {new Date(t.last_used_at).toLocaleString()}</>}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => revoke(t.token_id)}
                    className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    data-testid={`extension-revoke-${t.token_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="text-[11px] text-[#52525B] dark:text-[#A1A1AA] pt-2 border-t border-[#E4E4E7] dark:border-[#27272A]">
          <b>Güvenlik:</b> Eşleme kodu <b>10 dakika</b> içinde ve tek seferliktir. Kod alışverişi sonucu üretilen
          token yalnızca sizin tarayıcınızın güvenli deposunda tutulur. Token'ı buradan istediğiniz zaman
          iptal edebilirsiniz. Eklenti yalnızca <b>sahibinden.com</b>, <b>hepsiemlak.com</b> ve <b>emlakjet.com</b>
          alan adlarında sayfa verilerine erişebilir.
        </div>
      </CardContent>
    </Card>
  );
};

export default ExtensionSection;
